/**
 * DynamicForm.js
 *
 * A reusable component that takes a template's variable schema and
 * renders the appropriate input fields. Used at order time when a
 * client selects a DoqSolv template—only the *custom* variables
 * generate form fields (standard variables are auto‑populated from
 * the order itself).
 *
 * Features:
 *  - Visual grouping by `v.group` metadata (ungrouped → "General")
 *  - Data type–specific inputs (date picker, currency prefix, mobile validation)
 *  - Constraint enforcement (uppercase auto-convert, maxLength, required)
 *  - Pre-fills default values if provided
 *
 * Props:
 *  - variables: VariableDefinition[] (from the selected template)
 *  - values:    { [key]: value }     (current form state, controlled)
 *  - onChange:  (key, value) => void  (controlled handler)
 *  - readOnly?: boolean               (disable editing)
 *  - showAllVariables?: boolean        (if true, show standard + custom; used for standalone form filling)
 */

import React, { useEffect } from 'react';

const typeToInput = {
  text: 'text',
  number: 'number',
  date: 'date',
  email: 'email',
  phone: 'tel',
  mobile: 'tel',
  currency: 'number',
};

/**
 * Group variables by their `group` metadata.
 * Returns an array of { groupName, vars } objects, sorted with
 * ungrouped ("General") at the end.
 */
const groupVariables = (vars) => {
  const groups = {};
  vars.forEach((v) => {
    const g = v.group?.trim() || 'General';
    if (!groups[g]) groups[g] = [];
    groups[g].push(v);
  });

  // Sort: named groups first (alphabetical), "General" last
  const entries = Object.entries(groups);
  entries.sort(([a], [b]) => {
    if (a === 'General') return 1;
    if (b === 'General') return -1;
    return a.localeCompare(b);
  });

  return entries.map(([groupName, gVars]) => ({ groupName, vars: gVars }));
};

/**
 * Validate Indian mobile number: starts with 6-9, exactly 10 digits.
 */
const isValidMobile = (val) => /^[6-9]\d{9}$/.test(val);

const DynamicForm = ({ variables = [], values = {}, onChange, readOnly = false, showAllVariables = false }) => {
  // When showAllVariables is true (standalone filling), render everything.
  // Otherwise, only render custom variables (standard ones come from order data).
  const visibleVars = showAllVariables
    ? variables
    : variables.filter((v) => !v.is_standard);

  // Pre-fill default values on mount (only for fields not yet set)
  useEffect(() => {
    visibleVars.forEach((v) => {
      if (v.default_value && !values[v.key]) {
        let val = v.default_value;
        if (v.uppercase) val = val.toUpperCase();
        onChange(v.key, val);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (visibleVars.length === 0) {
    return (
      <div className="text-sm text-gray-400 italic py-2">
        {showAllVariables
          ? 'This template has no variables to fill.'
          : 'This template has no additional fields to fill — all variables will be auto‑populated from the order.'
        }
      </div>
    );
  }

  const groups = groupVariables(visibleVars);
  const hasMultipleGroups = groups.length > 1 || groups[0]?.groupName !== 'General';

  // Handle value change with constraint enforcement
  const handleChange = (v, rawValue) => {
    let value = rawValue;

    // Uppercase enforcement
    if (v.uppercase && typeof value === 'string') {
      value = value.toUpperCase();
    }

    // Max length enforcement
    if (v.max_length && typeof value === 'string' && value.length > v.max_length) {
      value = value.slice(0, v.max_length);
    }

    onChange(v.key, value);
  };

  // Render a single input field based on variable metadata
  const renderField = (v) => {
    const inputType = typeToInput[v.var_type] || 'text';
    const isTextarea = v.var_type === 'textarea';
    const isSelect = v.var_type === 'select';
    const isCurrency = v.var_type === 'currency';
    const isMobile = v.var_type === 'mobile';
    const currentValue = values[v.key] || '';

    // Mobile validation indicator
    const mobileInvalid = isMobile && currentValue && !isValidMobile(currentValue);

    return (
      <div key={v.key} className={isTextarea ? 'md:col-span-2' : ''}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {v.label}
          {v.is_required && <span className="text-red-500 ml-0.5">*</span>}
          {v.uppercase && (
            <span className="ml-1.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium">
              ABC
            </span>
          )}
          {v.max_length && (
            <span className="ml-1 text-[10px] text-gray-400">
              (max {v.max_length})
            </span>
          )}
        </label>

        {isTextarea ? (
          <textarea
            value={currentValue}
            onChange={(e) => handleChange(v, e.target.value)}
            readOnly={readOnly}
            rows={3}
            maxLength={v.max_length || undefined}
            required={v.is_required}
            placeholder={v.default_value || ''}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
          />
        ) : isSelect ? (
          <select
            value={currentValue}
            onChange={(e) => handleChange(v, e.target.value)}
            disabled={readOnly}
            required={v.is_required}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          >
            <option value="">Select…</option>
            {(v.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : isCurrency ? (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium">₹</span>
            <input
              type="number"
              value={currentValue}
              onChange={(e) => handleChange(v, e.target.value)}
              readOnly={readOnly}
              required={v.is_required}
              placeholder={v.default_value || '0'}
              min="0"
              step="0.01"
              className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
        ) : (
          <>
            <input
              type={inputType}
              value={currentValue}
              onChange={(e) => handleChange(v, e.target.value)}
              readOnly={readOnly}
              maxLength={v.max_length || undefined}
              required={v.is_required}
              placeholder={v.default_value || ''}
              pattern={isMobile ? '[6-9][0-9]{9}' : undefined}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none ${
                mobileInvalid ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            {mobileInvalid && (
              <p className="text-xs text-red-500 mt-1">
                Must start with 6-9 and be exactly 10 digits
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {groups.map(({ groupName, vars }) => (
        <div key={groupName}>
          {/* Group header (only show if we have meaningful groups) */}
          {hasMultipleGroups && (
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-2 flex items-center gap-2">
              <span className="w-3 h-px bg-gray-300" />
              {groupName}
              <span className="flex-1 h-px bg-gray-200" />
            </p>
          )}
          {!hasMultipleGroups && (
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">
              Template Fields
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vars.map(renderField)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DynamicForm;
